import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/enhanced-button";
import { useToast } from "@/hooks/use-toast";
import { initiateQuickBooksConnect, getIntegrations, type IntegrationStatus } from "@/services/integration.service";
import { getSyncStatus, startFullSync } from "@/services/sync.quickbooks.service";
import { CheckCircle2, TriangleAlert, RefreshCw } from "lucide-react";
import { format, parseISO, isPast, differenceInDays, formatDistanceToNow } from "date-fns";
import qbLogo from "@/assets/images/integrations/qb-logo.svg";

const QbDetails = ({ integration }: { integration: IntegrationStatus }) => {
  const rows: { label: string; value: React.ReactNode }[] = [];

  if (integration.externalCompanyName) {
    rows.push({ label: "Organization", value: integration.externalCompanyName });
  }

  if (integration.externalAccountId) {
    rows.push({ label: "Account ID", value: integration.externalAccountId });
  }

  if (integration.refreshTokenExpiresAt) {
    const expiry = parseISO(integration.refreshTokenExpiresAt);
    const expired = isPast(expiry);
    const daysLeft = differenceInDays(expiry, new Date());
    const dateColor = expired ? "text-red-600" : daysLeft <= 14 ? "text-amber-600" : "text-foreground";
    const badgeColor = expired
      ? "bg-red-50 text-red-600 border-red-200"
      : daysLeft <= 14
      ? "bg-amber-50 text-amber-600 border-amber-200"
      : "bg-emerald-50 text-emerald-600 border-emerald-200";
    rows.push({
      label: "Token expires",
      value: (
        <span className="flex flex-col items-end gap-0.5">
          <span className={`font-medium ${dateColor}`}>
            {format(expiry, "MMM d, yyyy")}
          </span>
          <span className={`inline-flex px-1.5 py-0.5 rounded border text-xs font-medium ${badgeColor}`}>
            {expired ? "Expired" : `${daysLeft}d remaining`}
          </span>
        </span>
      ),
    });
  }

  if (rows.length === 0) return null;

  return (
    <div className="space-y-2">
      {rows.map(({ label, value }) => (
        <div key={label} className="flex items-start justify-between gap-2">
          <span className="text-xs text-muted-foreground shrink-0 pt-0.5">{label}</span>
          <span className="text-xs text-right">{value}</span>
        </div>
      ))}
    </div>
  );
};

interface IntegrationsTabProps {
  hasIntegration: boolean;
  integrationError?: boolean;
}

const IntegrationsTab = ({ hasIntegration, integrationError = false }: IntegrationsTabProps) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [qbIntegration, setQbIntegration] = useState<IntegrationStatus | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncQueued, setSyncQueued] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const syncQueuedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    return () => {
      if (syncQueuedTimerRef.current) clearTimeout(syncQueuedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (hasIntegration) {
      getIntegrations().then((integrations) => {
        const qb = integrations.find((i) => i.provider === "quickbooks_online");
        if (qb) setQbIntegration(qb);
      }).catch(() => {});
    }
  }, [hasIntegration]);

  useEffect(() => {
    getSyncStatus().then((status) => {
      const last = status?.lastSync;
      if (last?.completedAt && (last.state === 'COMPLETED' || last.state === 'COMPLETED_WITH_ERRORS')) {
        setLastSyncAt(last.completedAt);
      }
    }).catch(() => {});
  }, []);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await startFullSync();
      setSyncQueued(true);
      if (syncQueuedTimerRef.current) clearTimeout(syncQueuedTimerRef.current);
      syncQueuedTimerRef.current = setTimeout(() => setSyncQueued(false), 3000);
    } catch {
      toast({
        title: "Sync Error",
        description: "Failed to start sync. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const authUrl = await initiateQuickBooksConnect();
      window.location.href = authUrl;
    } catch (error) {
      console.error("Failed to initiate QuickBooks connection:", error);
      toast({
        title: "Connection Error",
        description: "Failed to start QuickBooks connection. Please try again.",
        variant: "destructive",
      });
      setIsConnecting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-semibold text-foreground mb-6">Integrations</h1>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="border shadow-sm">
          <CardContent className="p-6 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <img src={qbLogo} alt="QuickBooks" className="w-10 h-10 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">QuickBooks Online</p>
                <p className="text-sm text-muted-foreground">Accounting & bookkeeping</p>
              </div>
            </div>

            {hasIntegration && !integrationError ? (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
                    <CheckCircle2 className="w-4 h-4" />
                    Connected
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-2 h-7"
                  >
                    Disconnect
                  </Button>
                </div>
                {qbIntegration && <QbDetails integration={qbIntegration} />}
                <p className="text-xs text-muted-foreground">
                  {lastSyncAt
                    ? `Last sync: ${format(parseISO(lastSyncAt), 'MMM d, yyyy h:mm a')} (${formatDistanceToNow(parseISO(lastSyncAt), { addSuffix: true })})`
                    : 'Never synced'}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSync}
                  disabled={isSyncing || syncQueued}
                  className="w-full gap-2"
                >
                  {isSyncing ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                      Starting sync...
                    </>
                  ) : syncQueued ? (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Sync queued
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3.5 h-3.5" />
                      Sync now
                    </>
                  )}
                </Button>
              </>
            ) : integrationError ? (
              <>
                <div className="flex items-center gap-2 text-sm text-amber-600 font-medium">
                  <TriangleAlert className="w-4 h-4" />
                  Authorization expired
                </div>
                <p className="text-xs text-muted-foreground">
                  Your QuickBooks session has expired. Reconnect to resume syncing.
                </p>
                <p className="text-xs text-muted-foreground">
                  {lastSyncAt
                    ? `Last sync: ${format(parseISO(lastSyncAt), 'MMM d, yyyy h:mm a')} (${formatDistanceToNow(parseISO(lastSyncAt), { addSuffix: true })})`
                    : 'Never synced'}
                </p>
                <Button
                  onClick={handleConnect}
                  disabled={isConnecting}
                  className="w-full"
                >
                  {isConnecting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    "Reconnect QuickBooks"
                  )}
                </Button>
              </>
            ) : (
              <Button
                onClick={handleConnect}
                disabled={isConnecting}
                className="w-full"
              >
                {isConnecting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Connecting...
                  </>
                ) : (
                  "Connect"
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

export default IntegrationsTab;
